import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  getDashboardExecutive, 
  getDashboardSales, 
  getDashboardFinance, 
  getDashboardProjects 
} from '../lib/api';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, 
  Briefcase, Target, AlertTriangle, CheckCircle2,
  ArrowUpRight, ArrowDownRight, Wallet, PieChart
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart as RePieChart, Pie, Cell, 
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CHART_COLORS = ['#E11D48', '#D97706', '#0F172A', '#10B981', '#6366F1'];

const formatCurrency = (amount, currency = 'ZAR') => {
  return new Intl.NumberFormat('en-ZA', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const KPICard = ({ title, value, change, changeType, icon: Icon, accentColor = 'text-slate-900' }) => (
  <Card className="kpi-card">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold font-mono ${accentColor}`}>{value}</p>
          {change !== undefined && (
            <div className="flex items-center gap-1">
              {changeType === 'up' ? (
                <ArrowUpRight className="h-4 w-4 text-emerald-500" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-rose-500" />
              )}
              <span className={`text-sm ${changeType === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                {change}%
              </span>
              <span className="text-sm text-muted-foreground">vs last month</span>
            </div>
          )}
        </div>
        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="h-5 w-5 text-slate-600" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const ExecutiveDashboard = () => {
  const [executive, setExecutive] = useState(null);
  const [sales, setSales] = useState(null);
  const [finance, setFinance] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [execRes, salesRes, finRes, projRes] = await Promise.all([
        getDashboardExecutive(),
        getDashboardSales(),
        getDashboardFinance(),
        getDashboardProjects()
      ]);
      setExecutive(execRes.data);
      setSales(salesRes.data);
      setFinance(finRes.data);
      setProjects(projRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  // Prepare chart data
  const pipelineData = sales?.pipeline_by_stage ? 
    Object.entries(sales.pipeline_by_stage).map(([name, value]) => ({
      name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value
    })) : [];

  const agingData = finance?.aging_analysis ?
    Object.entries(finance.aging_analysis).map(([name, value]) => ({
      name: name + ' days',
      value
    })) : [];

  const taskData = projects?.task_status ?
    Object.entries(projects.task_status).map(([name, value]) => ({
      name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value
    })) : [];

  return (
    <div className="space-y-8" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Executive Command Center</h1>
          <p className="text-muted-foreground mt-1">Real-time overview of your enterprise operations</p>
        </div>
        <Badge variant="outline" className="px-3 py-1">
          <span className="h-2 w-2 rounded-full bg-emerald-500 mr-2 animate-pulse" />
          Live
        </Badge>
      </div>

      {/* KPI Grid - Bento Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Pipeline Value"
          value={formatCurrency(executive?.sales?.pipeline_value || 0)}
          change={12}
          changeType="up"
          icon={Target}
        />
        <KPICard
          title="Monthly Revenue"
          value={formatCurrency(finance?.total_collected || 0)}
          change={8}
          changeType="up"
          icon={DollarSign}
          accentColor="text-amber-600"
        />
        <KPICard
          title="Outstanding"
          value={formatCurrency(finance?.total_outstanding || 0)}
          change={-5}
          changeType="down"
          icon={Wallet}
          accentColor="text-rose-600"
        />
        <KPICard
          title="Collection Rate"
          value={`${finance?.collection_rate || 0}%`}
          change={3}
          changeType="up"
          icon={TrendingUp}
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Pipeline */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-slate-600" />
              Sales Pipeline by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Value']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="value" fill="#0F172A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Receivables Aging */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Receivables Aging
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={agingData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {agingData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Project Status */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Project Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Active Projects</p>
                <p className="text-3xl font-bold font-mono">{projects?.total_projects || 0}</p>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Budget Utilization</p>
                <p className="text-3xl font-bold font-mono">{projects?.budget_utilization || 0}%</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Tasks Progress</span>
                <span className="font-mono">{projects?.task_status?.done || 0} / {projects?.total_tasks || 0}</span>
              </div>
              <Progress 
                value={projects?.total_tasks ? (projects.task_status?.done / projects.total_tasks) * 100 : 0} 
                className="h-2"
              />
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              <span className="text-sm text-rose-600 font-medium">
                {projects?.high_risk_count || 0} High Risk Items
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Task Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Task Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={taskData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {taskData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Sales Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Leads</span>
              <span className="font-mono font-semibold">{sales?.total_leads || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Opportunities</span>
              <span className="font-mono font-semibold">{sales?.total_opportunities || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Conversion Rate</span>
              <span className="font-mono font-semibold text-emerald-600">{sales?.conversion_rate || 0}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg Deal Size</span>
              <span className="font-mono font-semibold">{formatCurrency(sales?.avg_deal_size || 0)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grant Compliance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Grant Compliance</p>
                <p className="text-2xl font-bold font-mono">{executive?.grants?.compliance_score || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Grants</p>
                <p className="text-2xl font-bold font-mono text-amber-600">
                  {formatCurrency(executive?.grants?.total_grants || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Grants</p>
                <p className="text-2xl font-bold font-mono">{executive?.grants?.active_grants || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
